import { Play } from "lucide-react";

/**
 * Reusable Stories section (Why A Doctor / Celebrities).
 * Each card is a clickable link that opens the YouTube video in a new tab.
 */
export default function Stories({
  id,
  label,
  title,
  highlight,
  description,
  Icon,
  items,
  testid,
}) {
  return (
    <section
      id={id}
      data-testid={testid}
      className="relative py-24 lg:py-32 overflow-hidden bg-[#030508]/55"
    >
      <div className="absolute inset-0 bg-grid opacity-10 pointer-events-none" />
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[700px] h-[700px] rounded-full bg-[#00F0FF]/8 blur-[140px] pointer-events-none" />

      <div className="relative max-w-5xl mx-auto px-6 lg:px-10">
        {/* Header */}
        <div className="text-center mb-14">
          <div className="inline-flex items-center justify-center gap-3 mb-5">
            <span className="relative w-12 h-12 grid place-items-center border border-[#00F0FF]/40 bg-[#00F0FF]/5">
              <Icon className="w-5 h-5 text-[#00F0FF]" strokeWidth={2} />
            </span>
            <span className="text-label">{label}</span>
          </div>
          <h2 className="font-display font-black uppercase tracking-tighter text-4xl sm:text-5xl lg:text-6xl leading-[0.95]">
            {title}{" "}
            <span className="text-[#00F0FF] glow-cyan">{highlight}</span>
          </h2>
          <p className="mt-6 text-white/65 leading-relaxed max-w-2xl mx-auto">
            {description}
          </p>
        </div>

        {/* Clickable cards — each opens YouTube directly */}
        <div className="space-y-4">
          {items.map((item, i) => (
            <a
              key={item.youtubeId || i}
              href={`https://www.youtube.com/watch?v=${item.youtubeId}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid={`${testid}-item-${i}`}
              className="block glass-card group px-4 py-3 hover:bg-[#00F0FF]/5 transition-colors"
            >
              <div className="flex items-center gap-4">
                {/* YouTube thumbnail with play overlay */}
                <div className="relative w-28 sm:w-32 aspect-video flex-shrink-0 overflow-hidden border border-[#00F0FF]/30 bg-black">
                  <img
                    src={`https://i.ytimg.com/vi/${item.youtubeId}/mqdefault.jpg`}
                    alt={item.title}
                    loading="lazy"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 grid place-items-center bg-black/30 group-hover:bg-black/10 transition-colors">
                    <span className="w-10 h-10 grid place-items-center rounded-full bg-[#00F0FF] text-black shadow-[0_0_18px_#00F0FF] group-hover:scale-110 transition-transform">
                      <Play className="w-4 h-4 fill-black" strokeWidth={0} />
                    </span>
                  </div>
                </div>
                {/* Title */}
                <div className="flex items-center gap-3 min-w-0 flex-1">
                  <Icon
                    className="w-4 h-4 text-[#00F0FF] flex-shrink-0 hidden sm:block"
                    strokeWidth={2.2}
                  />
                  <span className="font-display font-bold text-sm sm:text-base lg:text-lg text-white text-left leading-tight">
                    {item.title}
                  </span>
                </div>
              </div>
            </a>
          ))}
        </div>
      </div>
    </section>
  );
}
